import { FC, useState } from 'react'
import { Form, redirect, useNavigate } from 'react-router-dom'
import './card.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import { BillingAddressFields } from '@components/forms'
import Button from '@components/atoms/button'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { parseAddress, parseKycStatus, parseTeraUserData } from '@schemas'
import { isNonEmptyString } from '@utils/type-checkers'
import { createAuthenticatedLoader, useLoader } from '@utils/loaders'
import { createAction } from '@utils/actions'


type OnAddressChangeArgs = Parameters<NonNullable<React.ComponentProps<typeof BillingAddressFields>['onAddressChange']>>

// 
// 
// Right now, we can't update a user's billing address in Tera so this page isn't used.
// Leaving this here in case we ever need to revisit this idea or Tera implements a 
// way to update a user from their api.
// 
// 
// 
const formId = 'update-billing-address'
const Component: FC<{}> = () => {
    const { address } = useLoader<typeof loader>()
    const [validBilling, setBillingIsValid] = useState(false)
    const navigate = useNavigate()

    function handleAddressChange(...[_, valid]: OnAddressChangeArgs) {
        setBillingIsValid(!!valid)
    }

    function handleBack() {
        navigate(-1)
    }

    return (<>
        <a onClick={handleBack}>
            <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
        </a>
        <div className="bubble-lrg" style={{ backgroundImage: "url(/icons/duotone/location.svg)" }} />
        <h3>Update Your Billing Address</h3>
        <p>This billing address is for your Splitcard virtual card</p>
        <Form id={formId} method="POST" style={{ border: '1px solid var(--splitcard-gray-200)', padding: '1.25rem', borderRadius: 'var(--border-radius-md)', gap: '0.625rem' }}>
            <BillingAddressFields initAddress={address} onAddressChange={handleAddressChange} />
        </Form>
        <Column.Footer>
            <Button form={formId} type="submit" disabled={!validBilling}>Confirm</Button>
        </Column.Footer>
    </>)
}

const loader = createAuthenticatedLoader(async ({ }, Clerk) => parseTeraUserData(Clerk.user?.publicMetadata.tera))

const action = createAction(async ({ request }) => {
    const formData = await request.formData()
    const user = window.Clerk?.user
    const address = parseAddress({
        line1: parseData('line1', formData) || null,
        line2: parseData('line2', formData) || null,
        city: parseData('city', formData) || null,
        state: parseData('state', formData) || null,
        postal_code: parseData('postal_code', formData) || null,
        country: parseData('country', formData) || null,
    })

    console.log("Update User Billing Data:", { address })
    if (!(
        isNonEmptyString(address.line1)
        && isNonEmptyString(address.city)
        && isNonEmptyString(address.state)
        && isNonEmptyString(address.postal_code)
        && isNonEmptyString(address.country)
    )) throw new Error(`Missing required resources. ${JSON.stringify(address)}`)

    if (user) user.publicMetadata = {
        kyc: parseKycStatus(user.publicMetadata.kyc),
        tera: parseTeraUserData({ ...user.publicMetadata.tera, address })
    }
    return redirect('../')
})

const parseData = (key: string, formData: FormData) => `${formData.get(key) || ''}` || undefined

const UpdateCardBillingRoute: RouteObject = {
    path: 'billing',
    Component,
    loader,
    action,
}

export { UpdateCardBillingRoute }
